import { useMemo } from 'react';

import type { Replacements } from '@change-corgi/core/i18n';
import { getPluralKey } from '@change-corgi/core/i18n';
import type { JsxReplacements } from '@change-corgi/core-react/i18n';
import {
	translatePluralHtml as translatePluralHtmlFn,
	useI18n,
	useI18nHtmlOptions,
} from '@change-corgi/core-react/i18n';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';

export type SpikingPetitionBannerTranslationOverrides = NonNullable<
	PetitionDetailsPageUserData['experiments']['spikingPetitionBanner']
>['experiment']['config']['translationOverrides'];

type PluralMap = NonNullable<NonNullable<SpikingPetitionBannerTranslationOverrides>['message']>;

const MESSAGE_KEY = 'corgi.spiking_petition_banner.message';
const PEOPLE_COUNT_KEY = 'corgi.spiking_petition_banner.people_count';
const PLURAL_CATEGORIES = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;

type Result = Readonly<{
	timeRange: () => string;
	cta: () => string;
	dismissLabel: () => string;
	peopleCount: (count: number, replacements: Replacements) => string;
	messageHtml: (count: number, replacements: JsxReplacements) => ReturnType<typeof translatePluralHtmlFn>;
}>;

/**
 * Translates the banner's strings, preferring the per-locale FCM `translationOverrides` over the regular translations
 * field by field. A field the overrides leave out uses the regular translation, which can be English for a locale
 * with no translations yet, so set every field to keep the banner in one language.
 * A plural map that lacks the locale's category falls back to its own `other`, not to a regular translation.
 */
export function useSpikingPetitionBannerTranslations(
	translationOverrides: SpikingPetitionBannerTranslationOverrides,
): Result {
	const { translate, translatePlural, replacePlaceholders, getLocale } = useI18n();
	const withHtmlDefaults = useI18nHtmlOptions();

	return useMemo(() => {
		function pickPluralOverride(key: string, map: PluralMap, count: number, replacements?: Replacements): string {
			// Keyed by the same full keys getPluralKey returns, so no output parsing is needed.
			const textByKey = new Map(PLURAL_CATEGORIES.map((category) => [`${key}.${category}`, map[category]]));
			return replacePlaceholders(textByKey.get(getPluralKey(getLocale(), key, count)) || map.other, replacements ?? {});
		}

		function translateMessage(_key: string, count: number, replacements?: Replacements): string {
			const map = translationOverrides?.message;
			return map
				? pickPluralOverride(MESSAGE_KEY, map, count, replacements)
				: translatePlural('corgi.spiking_petition_banner.message', count, replacements);
		}

		return {
			timeRange: () => translationOverrides?.timeRange || translate('corgi.spiking_petition_banner.time_range'),
			cta: () => translationOverrides?.cta || translate('corgi.spiking_petition_banner.cta'),
			dismissLabel: () =>
				translationOverrides?.dismissLabel || translate('corgi.spiking_petition_banner.dismiss_label'),
			peopleCount: (count, replacements) => {
				const map = translationOverrides?.peopleCount;
				return map
					? pickPluralOverride(PEOPLE_COUNT_KEY, map, count, replacements)
					: translatePlural('corgi.spiking_petition_banner.people_count', count, replacements);
			},
			messageHtml: (count, replacements) =>
				translatePluralHtmlFn(translateMessage, MESSAGE_KEY, count, replacements, withHtmlDefaults()),
		};
	}, [translationOverrides, translate, translatePlural, replacePlaceholders, getLocale, withHtmlDefaults]);
}
